import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #connectionToken = 0;

  /** @type {Set<AbortController>} */
  #activeFetches = new Set();

  /** @type {MutationObserver | null} */
  #filterUpdateObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #filterUpdateTimeout;

  /** @type {number | undefined} */
  #filterUpdateFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#connectionToken++;

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connectionToken++;
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    for (const controller of this.#activeFetches) controller.abort();
    this.#activeFetches.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.#cleanupFilterUpdateWait();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) {
            try {
              await viewTransition.current;
            } catch (error) {
              if (!(error instanceof DOMException && error.name === 'AbortError')) {
                console.error('Unable to finish the pagination view transition:', error);
              }
            }
          }
          if (!this.isConnected) return;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                void this.#renderPreviousPage().catch((error) => {
                  console.error('Unable to render the previous page:', error);
                });
              } else if (entry.target === viewMoreNext) {
                void this.#renderNextPage().catch((error) => {
                  console.error('Unable to render the next page:', error);
                });
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to fetch a paginated list page:', error);
      }
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const controller = new AbortController();
    this.#activeFetches.add(controller);
    const connectionToken = this.#connectionToken;
    try {
      const pageContent = await sectionRenderer.getSectionHTML(
        this.sectionId,
        true,
        pageInfo.url,
        controller.signal
      );
      if (!controller.signal.aborted && this.isConnected && connectionToken === this.#connectionToken) {
        this.pages.set(pageNumber, pageContent);
      }
    } finally {
      this.#activeFetches.delete(controller);
    }
  }

  async #renderNextPage() {
    const connectionToken = this.#connectionToken;
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      if (!this.isConnected || connectionToken !== this.#connectionToken) return;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    if (!this.isConnected || connectionToken !== this.#connectionToken) return;
    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      if (!this.isConnected || connectionToken !== this.#connectionToken) return;
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const connectionToken = this.#connectionToken;
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      if (!this.isConnected || connectionToken !== this.#connectionToken) return;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    if (!this.isConnected || connectionToken !== this.#connectionToken) return;

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      if (!this.isConnected || connectionToken !== this.#connectionToken) return;
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#cleanupFilterUpdateWait();
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const connectionToken = this.#connectionToken;
    let completed = false;
    const finishUpdate = () => {
      if (completed) return;
      completed = true;
      observer.disconnect();
      clearTimeout(this.#filterUpdateTimeout);
      this.#filterUpdateTimeout = undefined;
      if (this.#filterUpdateFrame !== undefined) cancelAnimationFrame(this.#filterUpdateFrame);
      this.#filterUpdateFrame = undefined;
      if (this.#filterUpdateObserver === observer) this.#filterUpdateObserver = null;
      if (!this.isConnected || connectionToken !== this.#connectionToken) return;
      this.infinityScrollObserver?.disconnect();
      this.#observeViewMore();
      this.#fetchPage('next');
    };
    const observer = new MutationObserver(() => {
      if (this.#filterUpdateFrame !== undefined) cancelAnimationFrame(this.#filterUpdateFrame);
      this.#filterUpdateFrame = requestAnimationFrame(() => {
        this.#filterUpdateFrame = undefined;
        finishUpdate();
      });
    });
    this.#filterUpdateObserver = observer;

    // Observe the grid for changes
    if (this.refs.grid) {
      observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
        subtree: true,
      });

      // Set a timeout as a fallback in case the mutation never fires
      this.#filterUpdateTimeout = setTimeout(finishUpdate, 3000);
    } else {
      this.#cleanupFilterUpdateWait();
    }
  };

  #cleanupFilterUpdateWait() {
    this.#filterUpdateObserver?.disconnect();
    this.#filterUpdateObserver = null;
    clearTimeout(this.#filterUpdateTimeout);
    this.#filterUpdateTimeout = undefined;
    if (this.#filterUpdateFrame !== undefined) cancelAnimationFrame(this.#filterUpdateFrame);
    this.#filterUpdateFrame = undefined;
  }
}
